import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CountrySelect } from '../ui/country-select';
import { InternationalPhoneInput } from '../ui/international-phone-input';
import { useUsers } from '@/api/users/use-users.ts';
import { useCustomAttributeDefinitionsManagement } from '@/hooks/useCustomAttributes.ts';
import DynamicInput from '../ui/DynamicInput';
import { normalizeCustomAttributesForForm } from '@/utils/customAttributes';
import { PHONE_REGEX, PHONE_VALIDATION_MESSAGE } from '@/utils/phoneValidation';

const customerSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email address'),
    phone: z
        .string()
        .min(1, 'Phone number is required')
        .regex(PHONE_REGEX, PHONE_VALIDATION_MESSAGE),
    additional_phone_number: z
        .string()
        .regex(PHONE_REGEX, PHONE_VALIDATION_MESSAGE)
        .optional()
        .or(z.literal('')),
    status: z.string().optional(),
    type: z.string().optional(),
    country: z.string().optional(),
    date_of_birth: z.string().optional(),
    preferred_language: z.string().optional(),
    time_zone: z.string().optional(),
    custom_attributes: z.record(z.any()).optional(),
    notes: z.string().optional(),
    assigned_user_id: z.string().optional(),
});

type CustomerFormData = z.infer<typeof customerSchema>;

interface CustomerFormProps {
    initialData?: Partial<CustomerFormData>;
    onSubmit: (data: CustomerFormData) => void;
    onCancel: () => void;
    isLoading?: boolean;
    mode?: 'create' | 'edit';
}

const CustomerForm: React.FC<CustomerFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    isLoading = false,
    mode = 'create',
}) => {
    // Fetch active users for assignee dropdown
    const { data: usersResponse } = useUsers({ status: 'active', per_page: 100 });
    const users = usersResponse?.data || [];

    // Fetch custom attribute definitions
    const { definitions: customAttributeDefinitions, isLoading: isLoadingCustomAttributes } =
        useCustomAttributeDefinitionsManagement();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<CustomerFormData>({
        resolver: zodResolver(customerSchema),
        defaultValues: {
            name: initialData?.name || '',
            email: initialData?.email || '',
            phone: initialData?.phone || '',
            additional_phone_number: initialData?.additional_phone_number || '',
            status: initialData?.status || 'active',
            country: initialData?.country || '',
            date_of_birth: initialData?.date_of_birth || '',
            preferred_language: initialData?.preferred_language || 'en',
            time_zone: initialData?.time_zone || '',
            custom_attributes:
                normalizeCustomAttributesForForm(initialData?.custom_attributes) || {},
            notes: initialData?.notes || '',
            assigned_user_id: initialData?.assigned_user_id || '',
        },
    });

    const onSubmitHandler = (data: CustomerFormData) => {
        const processedData = {
            ...data,
            assigned_user_id:
                !data.assigned_user_id || data.assigned_user_id === '__unassigned__'
                    ? undefined
                    : data.assigned_user_id,
        };
        onSubmit(processedData);
    };

    return (
        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="additional">Additional Info</TabsTrigger>
                    <TabsTrigger value="custom">Custom Attributes</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Basic Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name *</Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Enter name"
                                                className={errors.name ? 'border-red-500' : ''}
                                            />
                                        )}
                                    />
                                    {errors.name && (
                                        <p className="text-sm text-red-500">
                                            {errors.name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email *</Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type="email"
                                                placeholder="Enter email"
                                                className={errors.email ? 'border-red-500' : ''}
                                            />
                                        )}
                                    />
                                    {errors.email && (
                                        <p className="text-sm text-red-500">
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone *</Label>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        render={({ field }) => (
                                            <InternationalPhoneInput
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Enter phone number"
                                                className={errors.phone ? 'border-red-500' : ''}
                                            />
                                        )}
                                    />
                                    {errors.phone && (
                                        <p className="text-sm text-red-500">
                                            {errors.phone.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="additional_phone_number">
                                        Additional Phone Number
                                    </Label>
                                    <Controller
                                        name="additional_phone_number"
                                        control={control}
                                        render={({ field }) => (
                                            <InternationalPhoneInput
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Enter additional phone number"
                                                className={
                                                    errors.additional_phone_number
                                                        ? 'border-red-500'
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                    {errors.additional_phone_number && (
                                        <p className="text-sm text-red-500">
                                            {errors.additional_phone_number.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="country">Country</Label>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({ field }) => (
                                            <CountrySelect
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select country"
                                            />
                                        )}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                                    <Controller
                                        name="date_of_birth"
                                        control={control}
                                        render={({ field }) => <Input {...field} type="date" />}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select status" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="active">Active</SelectItem>
                                                    <SelectItem value="inactive">
                                                        Inactive
                                                    </SelectItem>
                                                    <SelectItem value="suspended">
                                                        Suspended
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="additional" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Additional Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="assigned_user_id">Assigned To</Label>
                                    <Controller
                                        name="assigned_user_id"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select assignee" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__unassigned__">
                                                        Unassigned
                                                    </SelectItem>
                                                    {users.map(user => (
                                                        <SelectItem key={user.id} value={user.id}>
                                                            {user.name}{' '}
                                                            {user.title && `(${user.title})`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="preferred_language">Preferred Language</Label>
                                    <Controller
                                        name="preferred_language"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select language" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="en">English</SelectItem>
                                                    <SelectItem value="ar">Arabic</SelectItem>
                                                    <SelectItem value="es">Spanish</SelectItem>
                                                    <SelectItem value="fr">French</SelectItem>
                                                    <SelectItem value="de">German</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="time_zone">Time Zone</Label>
                                    <Controller
                                        name="time_zone"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} placeholder="e.g., UTC+3" />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes</Label>
                                <Controller
                                    name="notes"
                                    control={control}
                                    render={({ field }) => (
                                        <textarea
                                            {...field}
                                            placeholder="Enter notes about this customer"
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="custom" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Custom Attributes</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {isLoadingCustomAttributes ? (
                                <div className="text-center py-4">
                                    <p className="text-muted-foreground">
                                        Loading custom attributes...
                                    </p>
                                </div>
                            ) : customAttributeDefinitions.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-muted-foreground">
                                        No custom attributes defined yet.
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Contact your administrator to set up custom attributes.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {customAttributeDefinitions.map(definition => (
                                        <Controller
                                            key={definition.id}
                                            name={`custom_attributes.${definition.name}`}
                                            control={control}
                                            render={({ field }) => (
                                                <DynamicInput
                                                    definition={definition}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    mode="form"
                                                    placeholder={`Enter ${definition.label.toLowerCase()}`}
                                                />
                                            )}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                    {isLoading
                        ? 'Saving...'
                        : mode === 'create'
                          ? 'Create Customer'
                          : 'Update Customer'}
                </Button>
            </div>
        </form>
    );
};

export default CustomerForm;
